import { FileSearch, MousePointerClick } from 'lucide-react';

interface EmptyLedgerStateProps {
  /** Type d'état vide */
  type?: 'no-selection' | 'no-results' | 'no-data';
  /** Titre personnalisé */
  title?: string;
  /** Description personnalisée */
  description?: string;
  /** Action optionnelle */
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Composant d'état vide professionnel pour le Grand Livre
 * Affiche une illustration et un message contextuel
 */
export function EmptyLedgerState({
  type = 'no-selection',
  title,
  description,
  action
}: EmptyLedgerStateProps) {
  const configs = {
    'no-selection': {
      icon: MousePointerClick,
      defaultTitle: 'Sélectionnez un compte',
      defaultDescription: 'Cliquez sur un compte dans la liste pour afficher ses mouvements comptables.',
      iconColor: 'text-primary'
    },
    'no-results': {
      icon: FileSearch,
      defaultTitle: 'Aucun résultat',
      defaultDescription: 'Aucun mouvement ne correspond à vos critères de recherche.',
      iconColor: 'text-amber-500'
    },
    'no-data': {
      icon: FileSearch,
      defaultTitle: 'Aucun mouvement',
      defaultDescription: 'Ce compte n\'a pas encore d\'écritures comptables.',
      iconColor: 'text-gray-400'
    }
  };

  const config = configs[type];
  const Icon = config.icon;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      {/* Illustration animée */}
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-primary/10 dark:bg-primary/5 rounded-full blur-2xl animate-pulse" />
        <div className={`
          relative p-6 rounded-2xl 
          bg-gradient-to-br from-gray-50 to-gray-100 
          dark:from-gray-800 dark:to-gray-900
          border border-gray-200 dark:border-gray-700
          shadow-lg
        `}>
          <Icon className={`h-12 w-12 ${config.iconColor} transition-transform hover:scale-110`} />
        </div>
      </div>

      {/* Texte */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {title || config.defaultTitle}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
        {description || config.defaultDescription}
      </p>

      {/* Action optionnelle */}
      {action && (
        <button
          onClick={action.onClick}
          className="mt-6 px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 
                     bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
        >
          {action.label}
        </button>
      )}

      {/* Indicateur visuel pour no-selection */}
      {type === 'no-selection' && (
        <div className="mt-8 flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          <div className="w-2 h-2 rounded-full bg-primary animate-ping" />
          <span>Liste des comptes sur la gauche</span>
        </div>
      )}
    </div>
  );
}
